import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { getInvoice } from '../../api/invoices'
import { useFetch } from '../../hooks/useFetch'
import { formatCurrency } from '@/lib/currency'
import type { InvoiceDetail } from '../../types/invoices'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function PrintLayout({ invoice }: { invoice: InvoiceDetail }) {
  useEffect(() => {
    document.title = invoice.invoice_number
    window.print()
    return () => {
      document.title = 'Vet VPMS'
    }
  }, [invoice.invoice_number])

  return (
    <div
      style={{
        fontFamily: 'Georgia, serif',
        color: '#111',
        maxWidth: '720px',
        margin: '0 auto',
        padding: '40px 32px',
        fontSize: '14px',
        lineHeight: '1.5',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', borderBottom: '2px solid #111', paddingBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', letterSpacing: '-0.5px' }}>Invoice</h1>
          <div style={{ fontFamily: 'monospace', fontSize: '18px', color: '#333', marginTop: '4px' }}>{invoice.invoice_number}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '13px', color: '#444' }}>
          <div><strong>Issued:</strong> {formatDate(invoice.issued_date)}</div>
          {invoice.due_date && (
            <div><strong>Due:</strong> {formatDate(invoice.due_date)}</div>
          )}
          {invoice.paid_at && (
            <div><strong>Paid:</strong> {formatDate(invoice.paid_at)}</div>
          )}
          <div style={{ marginTop: '8px' }}>
            <span style={{
              display: 'inline-block',
              padding: '2px 10px',
              border: '1px solid #999',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              {invoice.status}
            </span>
          </div>
        </div>
      </div>

      {/* Client / Patient */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#666', marginBottom: '4px' }}>Billed To</div>
          <div style={{ fontWeight: 'bold' }}>{invoice.client_name ?? `Client #${invoice.client_id}`}</div>
        </div>
        {invoice.patient_name && (
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#666', marginBottom: '4px' }}>Patient</div>
            <div style={{ fontWeight: 'bold' }}>{invoice.patient_name}</div>
          </div>
        )}
      </div>

      {/* Line items */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #111' }}>
            <th style={{ textAlign: 'left', padding: '6px 0', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', width: '70px' }}>Qty</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', width: '110px' }}>Unit Price</th>
            <th style={{ textAlign: 'right', padding: '6px 0', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', width: '110px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.line_items.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ padding: '12px 0', color: '#888', fontStyle: 'italic', textAlign: 'center' }}>No line items</td>
            </tr>
          ) : (
            invoice.line_items.map(item => (
              <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px 0' }}>{item.description}</td>
                <td style={{ textAlign: 'right', padding: '8px 8px', color: '#555' }}>{parseFloat(item.quantity).toFixed(2)}</td>
                <td style={{ textAlign: 'right', padding: '8px 8px', color: '#555' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: 'bold' }}>{formatCurrency(item.line_total)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
        <div style={{ width: '260px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#444' }}>
            <span>Subtotal (before discount)</span>
            <span>{formatCurrency((parseFloat(invoice.subtotal) + parseFloat(invoice.discount_amount)).toFixed(2))}</span>
          </div>
          {parseFloat(invoice.discount_amount) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#444' }}>
              <span>Discount</span><span>-{formatCurrency(invoice.discount_amount)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#444' }}>
            <span>Tax ({(parseFloat(invoice.tax_rate) * 100).toFixed(2)}%)</span>
            <span>{formatCurrency(invoice.tax_amount)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 'bold', fontSize: '16px', borderTop: '2px solid #111', marginTop: '4px' }}>
            <span>Total</span><span>{formatCurrency(invoice.total_amount)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '16px', marginTop: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#666', marginBottom: '6px' }}>Notes</div>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#333' }}>{invoice.notes}</p>
        </div>
      )}

      {/* Print-only close hint */}
      <div className="no-print" style={{ marginTop: '48px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
        Use your browser's print dialog (Ctrl+P / ⌘P) to save as PDF.
      </div>
    </div>
  )
}

export default function InvoicePrint() {
  const { id } = useParams<{ id: string }>()

  const { data: invoice, loading, error } = useFetch(
    () => getInvoice(Number(id)),
    [id],
  )

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#666' }}>
        Preparing invoice…
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#b00020' }}>
        {error ?? 'Invoice not found.'}
      </div>
    )
  }

  return <PrintLayout invoice={invoice} />
}
